/**
 * 操作日志模板解析工具
 * 用于解析操作描述中的模板字符串，将占位符替换为实际值
 */

import { credibilityList } from './constants';

/** 专家可信度枚举值 -> 展示文案（与 credibilityList 一致） */
const CREDIBILITY_VALUE_TO_LABEL = Object.fromEntries(credibilityList.map(({ value, label }) => [value, label]));

/**
 * 解析操作日志的模板字符串
 * @param {string} template - 包含占位符的模板字符串，如：'审核驳回，驳回理由：{{rejection_reason}}'
 * @param {Object|string} extra - 扩展信息，可能是JSON字符串或对象
 * @returns {string} - 解析后的描述文本
 */
export const parseOperationTemplate = (template, extra = null) => {
    if (!template) return '';

    // 如果没有占位符，直接返回原始模板
    if (!template.includes('{{') || !template.includes('}}')) {
        return template;
    }

    // 解析extra参数
    let extraData = {};
    if (extra) {
        try {
            extraData = typeof extra === 'string' ? JSON.parse(extra) : extra;
        } catch (error) {
            console.warn('解析extra数据失败:', error);
            return template;
        }
    }

    // 替换占位符
    let result = template;

    // 匹配所有的占位符 {{key}}
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    const matches = template.matchAll(placeholderRegex);

    for (const match of matches) {
        const fullMatch = match[0]; // 完整的占位符，如 {{rejection_reason}} 或 {{expertInfo.expertName}}
        const key = match[1].trim(); // 占位符的键，如 rejection_reason 或 expertInfo.expertName

        let value = null;

        // 支持嵌套属性访问，如 expertInfo.expertName
        if (key.includes('.')) {
            const keys = key.split('.');
            let current = extraData;
            for (const k of keys) {
                if (current && typeof current === 'object' && k in current) {
                    current = current[k];
                } else {
                    current = null;
                    break;
                }
            }
            value = current;
        } else {
            // 简单的键值访问
            value = extraData[key];
        }

        // 专家可信度：extra 存枚举值，展示时按变量列表转为中文
        if (key === 'from_credibility' || key === 'to_credibility') {
            if (value == null || value === '') {
                value = '未知';
            } else {
                value = CREDIBILITY_VALUE_TO_LABEL[String(value)] ?? String(value);
            }
        }

        // 特殊处理：如果值是对象，尝试格式化
        if (value && typeof value === 'object') {
            // 处理 transportInfo、carInfo、hotelInfo、ticketInfo 等特殊对象
            if (key === 'transportInfo' && Array.isArray(extraData.transports)) {
                // 格式化交通信息
                const transports = extraData.transports;
                value = transports
                    .map(transport => {
                        const parts = [];
                        if (transport.transport_type_text) parts.push(transport.transport_type_text);
                        if (transport.transport_no) parts.push(transport.transport_no);
                        if (transport.seat_type) parts.push(transport.seat_type);
                        if (transport.departure_place && transport.arrival_place) {
                            parts.push(`${transport.departure_place}→${transport.arrival_place}`);
                        }
                        if (transport.departure_datetime && transport.arrival_datetime) {
                            const formatTime = dt => {
                                try {
                                    const d = new Date(dt);
                                    const h = String(d.getHours()).padStart(2, '0');
                                    const m = String(d.getMinutes()).padStart(2, '0');
                                    return `${h}:${m}`;
                                } catch {
                                    return dt;
                                }
                            };
                            parts.push(`${formatTime(transport.departure_datetime)}～${formatTime(transport.arrival_datetime)}`);
                        }
                        return parts.join(' ');
                    })
                    .join('，');
            } else if (key === 'carInfo' && value.formatted) {
                // 使用格式化后的字符串
                value = value.formatted;
            } else if (key === 'hotelInfo' && value.formatted) {
                // 使用格式化后的字符串
                value = value.formatted;
            } else if (key === 'ticketInfo' && extraData.transportInfo) {
                // 格式化票信息
                const transportInfo = extraData.transportInfo;
                const directionText = transportInfo.direction === 'arrival' ? '来程交通' : '返程交通';
                if (transportInfo.transports && transportInfo.transports.length > 0) {
                    const transport = transportInfo.transports[0];
                    const parts = [directionText];
                    if (transport.transport_type_text) parts.push(transport.transport_type_text);
                    if (transport.transport_no) parts.push(transport.transport_no);
                    if (transport.seat_type) parts.push(transport.seat_type);
                    value = parts.join(' ');
                } else {
                    value = '无';
                }
            } else {
                // 其他对象，尝试转换为字符串
                value = JSON.stringify(value);
            }
        }

        if (value !== undefined && value !== null && value !== '') {
            result = result.replace(fullMatch, String(value));
        } else {
            // 如果没有找到对应的值，保留原始占位符或替换为空字符串
            console.warn(`操作日志模板解析：未找到占位符 ${key} 对应的值`);
            result = result.replace(fullMatch, `[${key}]`); // 用 [key] 表示缺失的值
        }
    }

    return result;
};

/**
 * 格式化操作日志显示
 * @param {Object} log - 日志对象
 * @param {string} log.operationDesc - 操作描述模板
 * @param {string|Object} log.extra - 扩展信息
 * @param {string} log.createdAt - 创建时间
 * @param {string} log.adminId - 管理员ID
 * @param {string} log.adminName - 管理员名称
 * @param {string} log.memcard - 用户ID
 * @param {string} log.result - 操作结果
 * @returns {Object} - 格式化后的日志显示对象
 */
export const formatOperationLogDisplay = log => {
    // 获取操作者信息
    let operator = '系统';
    if (log.adminId) {
        // 管理员操作
        operator = `管理员 ${log.adminName || ''}`.trim();
    } else if (log.memcard) {
        // 用户操作
        operator = '用户';
    }

    // 获取操作时间，直接使用接口返回的格式
    const operationTime = log.createdAt || '';

    // 解析操作描述模板
    const description = parseOperationTemplate(log.operationDesc, log.extra);

    return {
        time: operationTime,
        operator,
        description,
        result: log.result || 'success',
    };
};
